import {
  memo,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

// Dynamic row measurement for react-virtual. A position row is a Fragment of
// a main `<TableRow data-index=i>` plus, when expanded, a sibling breakdown
// `<TableRow data-expansion-index=i>`. react-virtual measures one element
// per item, so the item's true height is the main row's height PLUS the
// expansion sibling's when it's mounted — otherwise the expanded panel isn't
// counted and the bottom becomes unreachable while a row is open.
const measureRowWithExpansion = (el: Element): number => {
  const main = el as HTMLElement;
  let height = main.offsetHeight;
  const sibling = main.nextElementSibling as HTMLElement | null;
  if (
    sibling &&
    sibling.getAttribute('data-expansion-index') ===
      main.getAttribute('data-index')
  ) {
    height += sibling.offsetHeight;
  }
  return height;
};
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckIcon from '@mui/icons-material/Check';
import PublishIcon from '@mui/icons-material/Publish';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { EnginePositionDrawer } from '../components/EnginePositionDrawer';

import { usePositionsGrouped, useUpdatePairExpiry } from '../api/usePositions';
import {
  useUnwindingRules,
  usePanic,
  usePanicRecent,
  useGlobalToggle,
  useTogglePlatform,
  useCreateRule,
  useUpdateRule,
  useSetSimParams,
  useApplySim
} from '../api/useUnwinding';
import { useSystemConfig } from '../api/useSystemConfig';
import { HedgeModeGlobalControls } from '../components/shared/HedgeModeGlobalControls';
import { StopStartUnwinderButtons } from '../components/unwinding/StopStartUnwinderButtons';
import { UnwindingTabSumOfSides } from '../components/unwinding/UnwindingTabSumOfSides';
import { PredictionPosition, PositionsGroupedByPair } from '../types';
import { UnwindingRule, SimulationResult, UpdateUnwindingRuleDto } from '../types/unwinding.types';
import { useUnwindingSocket, UnwindLiveStore } from '../hooks/useUnwindingSocket';
import { createSingleFlight } from '../api/ruleCreation';
import { buildRulesByPairId } from '../utils/rulesByPairId';
import { useLiveState } from '../live/useLiveStore';
import { NetCell, RestNetCell } from '../live/NetCell';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Format price with 3 decimal places (matching platform precision).
 *  0.958 → "0.958", 0.95 → "0.950", 0.07 → "0.070", 0.016 → "0.016", 0.0046 → "0.0046" */
const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '\u2014';
  return `${(v * 100).toFixed(1)}¢`;
};

const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '\u2014';
  if (v <= 0) return '\u2014';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return v.toFixed(2);
};

const isoToLocalInput = (iso?: string | Date | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const tzOffsetMs = date.getTimezoneOffset() * 60_000;
  const local = new Date(date.getTime() - tzOffsetMs);
  return local.toISOString().slice(0, 16);
};

const localInputToUtcIso = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
};



/** Format position contracts: integer count, no commas (contracts are whole units) */
const fmtContracts = (v: number | undefined | null): string => {
  if (v == null || v === 0) return '\u2014';
  return Math.round(v).toString();
};

// Pick the dominant open position for a platform — the one with the largest
// contract count. The upstream sometimes returns multiple open documents per
// platform after a side flip; picking the *first* match leaves the choice up
// to array order and can disagree with what the position-unwinder service
// resolves to, producing chip/price-side mismatch on the row.
const findPos = (group: PositionsGroupedByPair, platform: 'polymarket' | 'kalshi'): PredictionPosition | undefined => {
  let best: PredictionPosition | undefined;
  let bestContracts = 0;
  for (const p of group.positions) {
    if (p.platform !== platform || p.status !== 'open') continue;
    const contracts = Number(p.contracts) || 0;
    if (contracts <= 0) continue;
    if (contracts > bestContracts) {
      best = p;
      bestContracts = contracts;
    }
  }
  return best;
};

/** Get max position size for sorting */
const getMaxPositionSize = (group: PositionsGroupedByPair): number => {
  const poly = findPos(group, 'polymarket');
  const kalshi = findPos(group, 'kalshi');
  return Math.max(poly?.contracts ?? 0, kalshi?.contracts ?? 0);
};

const hasAnyContracts = (group: PositionsGroupedByPair): boolean => {
  const poly = findPos(group, 'polymarket');
  const kalshi = findPos(group, 'kalshi');
  return (poly?.contracts ?? 0) > 0 || (kalshi?.contracts ?? 0) > 0;
};

// Proportional column widths for the TNA tab's table, fed to a
// <colgroup>. Same pattern as `UnwindingTabSumOfSides.tsx`: under
// `tableLayout: 'fixed'` with every cell carrying an explicit pixel
// `width:`, the column widths sum to ~791 px and Chrome renders the
// table at that natural size instead of stretching to fill the
// container. Listing the widths once as percentages of the total gives
// every column its proportional share of 100% of the table width.
// Order matches the rendered column sequence: Market · Pos · NET · ACT · 8 Market
// Data (Qty/P.Bid/P.Ask/Qty/Qty/K.Bid/K.Ask/Qty) · 3 Parameters
// (TNA/Size/MaxV) · 4 Simulation (K.Bid/P.Ask/P.Bid/K.Ask) · 2 Ctrl
// (P/K).
const TNA_COL_WIDTHS_PX = [
  240, 45, 40, 40,
  24, 32, 32, 24, 24, 32, 32, 24,
  32, 30, 36,
  32, 32, 32, 32,
  28, 28,
] as const;
const TNA_COL_TOTAL_PX = TNA_COL_WIDTHS_PX.reduce((s, w) => s + w, 0);

const hSx = {
  fontSize: '0.58rem', fontWeight: 600, color: 'grey.500',
  py: 0.2, px: 0.1, textTransform: 'uppercase' as const,
  letterSpacing: '0.02em', whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const gSx = { ...hSx, textAlign: 'center' as const, borderBottom: '1px solid rgba(255,255,255,0.1)' };
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.05 } as const;

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export const UnwindingPage = () => {
  const { data: positionsData, isLoading } = usePositionsGrouped({ status: 'open' });
  const updatePairExpiryMutation = useUpdatePairExpiry();
  const { data: rules } = useUnwindingRules();
  const panicMutation = usePanic();
  const panicRecentMutation = usePanicRecent();
  const globalToggleMutation = useGlobalToggle();
  const togglePlatformMutation = useTogglePlatform();
  const createRuleMutation = useCreateRule();
  const updateRuleMutation = useUpdateRule();
  const simParamsMutation = useSetSimParams();
  const applySimMutation = useApplySim();
  const { isConnected, getState, injectStates, store } = useUnwindingSocket();
  const { data: systemConfig } = useSystemConfig();
  const [expandedPairId, setExpandedPairId] = useState<string | null>(null);
  // Drawer state for the per-rule "Engine position" view — the live engine's
  // believed contracts per (path, platform), read off the same liveState the
  // row already holds.
  const [enginePos, setEnginePos] = useState<{ ruleId: string; pairName: string } | null>(null);
  // Sum of Sides is the operator-facing default — the TNA tab is kept for
  // legacy / debug review of the older rule type. URL ?tab=tna still works
  // because the Tabs control respects the value prop.
  const [tab, setTab] = useState<'tna' | 'sum_of_sides'>('sum_of_sides');
  // Free-text search over pair titles. Matches `pairName`,
  // `kalshiTitle`, `polymarketTitle` (case-insensitive substring) so
  // the operator can narrow a long position list to the rule they
  // care about. Empty / whitespace-only string disables the filter.
  const [search, setSearch] = useState('');

  const globalEnabled = Array.isArray(rules) && rules.some(r => r.status === 'active');

  // Per-tab rule slices. Legacy docs without ruleType default to 'tna'.
  const tnaRules = useMemo(
    () => (rules ?? []).filter(r => (r.ruleType ?? 'tna') === 'tna'),
    [rules]
  );
  const sumRules = useMemo(
    () => (rules ?? []).filter(r => r.ruleType === 'sum_of_sides'),
    [rules]
  );

  // Duplicate-aware row binding (shared helper with the opener): prefers
  // the ACTIVE rule when the duplicate-create race left orphans and warns
  // once per pair instead of silently binding last-write-wins.
  const tnaRulesByPairId = useMemo(
    () => buildRulesByPairId(tnaRules, { scope: 'unwinding' }),
    [tnaRules]
  );

  // Any rule keyed by pairId — used by the dev-logs button so it works
  // for sum_of_sides rules on the TNA tab too. The expanded row's other
  // controls (sim params, platform toggle) intentionally still use
  // `tnaRulesByPairId` because they're TNA-tab-specific. Picks the
  // active rule first when a pair somehow has multiple, falling back to
  // the most recently updated otherwise.
  const anyRulesByPairId = useMemo(() => {
    const map = new Map<string, UnwindingRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      const existing = map.get(rule.pairId);
      if (!existing) {
        map.set(rule.pairId, rule);
        continue;
      }
      const incomingActive = rule.status === 'active';
      const existingActive = existing.status === 'active';
      if (incomingActive && !existingActive) {
        map.set(rule.pairId, rule);
      }
    }
    return map;
  }, [rules]);

  // Single-flight create per pair+ruleType: every rule-less toggle (the sum
  // tab's Kalshi/Poly switches, the TNA tab's platform toggle) funnels
  // through this map, so at most ONE POST /rules is in flight per pair. A
  // second toggle landing before the refetch joins the in-flight create's
  // promise and applies its platform via the normal toggle-platform PATCH
  // instead of creating a duplicate rule.
  const inFlightCreates = useRef(createSingleFlight<UnwindingRule>());

  const handleCreateSumRule = useCallback(async ({ group, platform, thresholdSum, size, maxVolumeContracts, makerSizeMode, counterHedgeMode, counterStuckMarketHedgeSeconds, counterMakerWalkStepSeconds, counterMakerWalkStepCents, lockstepMaxAheadBatches }: {
    group: PositionsGroupedByPair;
    platform: 'kalshi' | 'polymarket';
    thresholdSum: number;
    size: number;
    maxVolumeContracts: number;
    makerSizeMode?: 'dynamic' | 'fixed';
    counterHedgeMode?:
      | 'always-threshold'
      | 'gtc-to-max-sum'
      | 'market-to-hedge'
      | null;
    counterStuckMarketHedgeSeconds?: number | null;
    counterMakerWalkStepSeconds?: number | null;
    counterMakerWalkStepCents?: number | null;
    lockstepMaxAheadBatches?: number | null;
  }) => {
    const pairId = group._id;
    if (!pairId) return;
    const pp = findPos(group, 'polymarket');
    const kp = findPos(group, 'kalshi');
    // Platform flags are NEVER baked into the create: a flagged create
    // auto-activates server-side, so a second toggle joining this flight
    // would skip its own activation gate. Rules are born paused with both
    // platforms off; each toggle then applies its platform via the normal
    // toggle-platform PATCH below — the same activation path (gate
    // included) a rule-ful toggle takes.
    const rule = await inFlightCreates.current.ensure(
      `${pairId}:sum_of_sides`,
      () =>
        createRuleMutation.mutateAsync({
          pairId,
          pairName: group.pairName,
          expiryDate: group.expiryDate || new Date().toISOString(),
          ruleType: 'sum_of_sides',
          thresholdSum,
          kalshiConfig: kp
            ? { side: kp.side, ticker: kp.marketId, contractsToSell: size }
            : undefined,
          polymarketConfig: pp
            ? {
                side: pp.side,
                tokenId: pp.marketId,
                conditionId: pp.marketId,
                contractsToSell: size
              }
            : undefined,
          maxVolumeContracts,
          volumePeriod: 'day',
          kalshiEnabled: false,
          polymarketEnabled: false,
          ...(makerSizeMode ? { makerSizeMode } : {}),
          // Pass an explicit `null` (inherit) through — `undefined` is the
          // only "not provided" value. Defends against backends predating
          // the null schema default, which pinned omitted fields to the non-null default.
          ...(counterHedgeMode !== undefined ? { counterHedgeMode } : {}),
          ...(counterStuckMarketHedgeSeconds != null
            ? { counterStuckMarketHedgeSeconds }
            : {}),
          ...(counterMakerWalkStepSeconds != null ? { counterMakerWalkStepSeconds } : {}),
          ...(counterMakerWalkStepCents != null ? { counterMakerWalkStepCents } : {}),
          ...(lockstepMaxAheadBatches != null ? { lockstepMaxAheadBatches } : {})
        })
    );
    await togglePlatformMutation.mutateAsync({
      id: rule._id,
      platform,
      enabled: true
    });
  }, [createRuleMutation, togglePlatformMutation]);

  const handleToggleRulePlatform = useCallback(
    async (rule: UnwindingRule, platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      await togglePlatformMutation.mutateAsync({ id: rule._id, platform, enabled });
    },
    [togglePlatformMutation],
  );

  const handleUpdateRule = useCallback(async (rule: UnwindingRule, dto: UpdateUnwindingRuleDto) => {
    try {
      await updateRuleMutation.mutateAsync({ id: rule._id, dto });
      return true;
    } catch {
      return false;
    }
  }, [updateRuleMutation]);

  const handleTogglePlatform = useCallback(
    async (rule: UnwindingRule | undefined, group: PositionsGroupedByPair, platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      if (rule) {
        await togglePlatformMutation.mutateAsync({ id: rule._id, platform, enabled });
        return;
      }
      if (!enabled) return;
      const pairId = group._id;
      if (!pairId) return;
      const pp = findPos(group, 'polymarket');
      const kp = findPos(group, 'kalshi');
      // Same ensure-then-toggle funnel as `handleCreateSumRule` (see the
      // single-flight comment there): rule born paused with both platforms
      // off, then the requested platform is applied via toggle-platform.
      const created = await inFlightCreates.current.ensure(
        `${pairId}:tna`,
        () =>
          createRuleMutation.mutateAsync({
            pairId,
            pairName: group.pairName,
            expiryDate: group.expiryDate || new Date().toISOString(),
            kalshiConfig: kp ? { side: kp.side, ticker: kp.marketId, contractsToSell: 50, targetTnaPercent: 5 } : undefined,
            polymarketConfig: pp ? { side: pp.side, tokenId: pp.marketId, conditionId: pp.marketId, contractsToSell: 50, targetTnaPercent: 5 } : undefined,
            maxVolumeContracts: 500,
            volumePeriod: 'day',
            kalshiEnabled: false,
            polymarketEnabled: false
          })
      );
      await togglePlatformMutation.mutateAsync({
        id: created._id,
        platform,
        enabled: true
      });
    },
    [createRuleMutation, togglePlatformMutation]
  );

  const handleSaveParams = useCallback(async (
    pairId: string | null | undefined,
    params: { tna: number; size: number; maxVol?: number }
  ) => {
    if (!pairId) return false;
    try {
      const data: any = await simParamsMutation.mutateAsync({
        pairId,
        tna: params.tna,
        size: params.size,
        maxVol: params.maxVol
      });
      if (Array.isArray(data?.states) && data.states.length > 0) {
        injectStates(data.states);
      } else if (data?.state) {
        injectStates([data.state]);
      }
      return true;
    } catch {
      return false;
    }
  }, [injectStates, simParamsMutation]);

  const handleApplySim = useCallback(async (pairId: string | null | undefined) => {
    if (!pairId) return false;
    try {
      const data: any = await applySimMutation.mutateAsync({ pairId });
      if (data?.state) injectStates([data.state]);
      return true;
    } catch {
      return false;
    }
  }, [applySimMutation, injectStates]);

  const handleUpdateExpiry = useCallback(async (pairId: string, expiryDate: string) => {
    try {
      await updatePairExpiryMutation.mutateAsync({ pairId, expiryDate });
      return true;
    } catch {
      return false;
    }
  }, [updatePairExpiryMutation]);

  const handleToggleExpand = useCallback((pairKey: string) => {
    setExpandedPairId(prev => prev === pairKey ? null : pairKey);
  }, []);

  const handleOpenEnginePosition = useCallback(
    (ruleId: string, pairName: string) => {
      setEnginePos({ ruleId, pairName });
    },
    [],
  );
  const handleCloseEnginePosition = useCallback(() => setEnginePos(null), []);

  // Sort by max position size descending
  const groups = useMemo(() => {
    if (!positionsData) return [];
    const allGroups = [
      ...(positionsData.groups ?? []),
      ...(positionsData.unmatched ?? []),
    ];
    return allGroups
      .filter(g => (findPos(g, 'polymarket') || findPos(g, 'kalshi')) && hasAnyContracts(g))
      .sort((a, b) => getMaxPositionSize(b) - getMaxPositionSize(a));
  }, [positionsData]);

  // Search filter: case-insensitive substring match across the three
  // title fields a group can carry. Applied after the size sort so the
  // visible order stays stable. Empty / whitespace-only query is a
  // no-op (returns the full list unchanged).
  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g: any) => {
      const name = (g.pairName ?? '').toLowerCase();
      const kt = (g.kalshiTitle ?? '').toLowerCase();
      const pt = (g.polymarketTitle ?? '').toLowerCase();
      return name.includes(q) || kt.includes(q) || pt.includes(q);
    });
  }, [groups, search]);

  // ─── Row virtualization (2026-05-23) ──────────────────────────────────
  // Mount only the visible `PositionRow`s + a top/bottom spacer. Per
  // ~/.claude/plans/smooth-twirling-lampson.md, no-virtualization with
  // MUI's heavy `<TableRow>` × ~19 `<TableCell>` rows was the dominant
  // retained-memory cost on this page.
  //
  // The expanded row (SimulationBreakdownPanel) renders as a SECOND
  // TableRow inside the PositionRow Fragment. Real row heights vary (1- vs
  // 2-line market titles, the expansion panel), so a fixed/estimated size
  // under-counted total height and the last rows fell outside the
  // scrollable range ("can't reach the bottom"). Each row now reports its
  // real height via `measureElement`, summing the expansion sibling when
  // open (see `measureRowWithExpansion`), so `getTotalSize()` is exact.
  // `estimateSize` stays as the pre-measurement seed only.
  const tnaTableContainerRef = useRef<HTMLDivElement>(null);
  const tnaRowVirtualizer = useVirtualizer({
    count: visibleGroups.length,
    getScrollElement: () => tnaTableContainerRef.current,
    estimateSize: index => {
      const group = visibleGroups[index] as any;
      const pairKey = group?._id ?? group?.pairName ?? String(index);
      // ~52px collapsed; ~320px when the SimulationBreakdownPanel +
      // expiry picker are mounted.
      return expandedPairId === pairKey ? 320 : 52;
    },
    measureElement: measureRowWithExpansion,
    overscan: 6,
    // `getItemKey` keeps virtual-item identity stable across re-renders
    // so React-tree reconciliation doesn't churn the visible rows on
    // every WS tick.
    getItemKey: index => {
      const group = visibleGroups[index] as any;
      return group?._id ?? group?.pairName ?? index;
    },
  });
  // Re-measure the mounted rows when the expanded pair changes. Toggling an
  // expansion mounts/unmounts a sibling `<TableRow>` WITHOUT resizing the
  // main row, so the ResizeObserver behind `measureElement` doesn't fire on
  // its own. Re-running `measureElement` over every mounted row refreshes
  // the newly-expanded row (now summing its panel) AND the just-collapsed
  // one — without wiping the measurement cache the way `measure()` would.
  useLayoutEffect(() => {
    const container = tnaTableContainerRef.current;
    if (!container) return;
    container
      .querySelectorAll<HTMLTableRowElement>('tr[data-index]')
      .forEach(node => tnaRowVirtualizer.measureElement(node));
  }, [expandedPairId, tnaRowVirtualizer]);

  return (
    <Box sx={{ px: 2, pt: 1, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Title bar */}
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'white' }}>Position Unwinding</Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          {systemConfig && (
            <Chip
              label={`Engine: ${systemConfig.unwinderEngine === 'rust' ? 'Rust' : 'Node'}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          <HedgeModeGlobalControls />
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: '0.6rem !important' }} />}
            label={isConnected ? 'Live' : 'Disconnected'}
            size="small" color={isConnected ? 'success' : 'default'} variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={globalEnabled} onChange={(_, v) => globalToggleMutation.mutate(v)} />}
            label="Global"
            sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' }, mr: 0 }}
          />
          <StopStartUnwinderButtons />
          <Button
            variant="contained" color="warning" size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => panicRecentMutation.mutate()}
            sx={{ fontSize: '0.7rem' }}
            disabled={panicRecentMutation.isLoading}
            title="Cancel orders + pause rules active in the last 24h. Orders on unrelated markets are NOT touched."
          >{panicRecentMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'PANIC 24H'}</Button>
          <Button
            variant="contained" color="error" size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => panicMutation.mutate()}
            sx={{ fontSize: '0.7rem' }}
            disabled={panicMutation.isLoading}
          >{panicMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'PANIC'}</Button>
        </Stack>
      </Stack>

      {/* Search + Tab selector — search filters BOTH tabs by pair title */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
          }}
          inputProps={{ 'aria-label': 'Search rules / positions by title' }}
        />
        <Tabs
          value={tab}
          onChange={(_, v: 'tna' | 'sum_of_sides') => setTab(v)}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.75rem' }
          }}
        >
          <Tab value="sum_of_sides" label="Sum of Sides" />
          <Tab value="tna" label="TNA" />
        </Tabs>
        {search.trim() && (
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
            {visibleGroups.length}/{groups.length} matched
          </Typography>
        )}
      </Stack>

      {/* Positions table — scrollable with sticky headers */}
      <Box sx={{ flex: 3, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : tab === 'sum_of_sides' ? (
          <UnwindingTabSumOfSides
            groups={visibleGroups}
            rules={sumRules}
            anyRulesByPairId={anyRulesByPairId}
            store={store}
            onCreateRule={handleCreateSumRule}
            onTogglePlatform={handleToggleRulePlatform}
            onUpdateRule={handleUpdateRule}
            onOpenEnginePosition={handleOpenEnginePosition}
          />
        ) : visibleGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
              {groups.length === 0
                ? 'No open positions found.'
                : `No positions match “${search.trim()}”.`}
            </Typography>
          </Box>
        ) : (
          <TableContainer
            ref={tnaTableContainerRef}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          >
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                {TNA_COL_WIDTHS_PX.map((w, i) => (
                  <col key={i} style={{ width: `${(w / TNA_COL_TOTAL_PX) * 100}%` }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...gSx, width: 240, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2}>Market</TableCell>
                  <TableCell sx={{ ...gSx, width: 45, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2}>Pos</TableCell>
                  <Tooltip title="Live engine NET: total YES − total NO. Red when unhedged (>0.5)." placement="top">
                    <TableCell sx={{ ...gSx, width: 40, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2} align="right">NET</TableCell>
                  </Tooltip>
                  <Tooltip title="Actual held NET from positions (total YES − total NO). Independent of the live engine — surfaces a real leg imbalance the engine belief reads as 0 for a pre-existing position." placement="top">
                    <TableCell sx={{ ...gSx, width: 40, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2} align="right">ACT</TableCell>
                  </Tooltip>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={8}>Market Data</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={3}>Parameters</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={4}>Simulation</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={2}>Ctrl</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">TNA</TableCell>
                  <TableCell sx={{ ...hSx, width: 30, top: 22, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
                  <TableCell sx={{ ...hSx, width: 36, top: 22, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">P</TableCell>
                  <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">K</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const virtualItems = tnaRowVirtualizer.getVirtualItems();
                  const totalSize = tnaRowVirtualizer.getTotalSize();
                  const paddingTop =
                    virtualItems.length > 0 ? virtualItems[0].start : 0;
                  const paddingBottom =
                    virtualItems.length > 0
                      ? totalSize -
                        virtualItems[virtualItems.length - 1].end
                      : 0;
                  return (
                    <>
                      {paddingTop > 0 && (
                        <TableRow style={{ height: paddingTop }}>
                          <TableCell
                            colSpan={100}
                            sx={{ p: 0, border: 'none' }}
                            aria-hidden
                          />
                        </TableRow>
                      )}
                      {virtualItems.map(vi => {
                        const group = visibleGroups[vi.index];
                        const rule = group._id
                          ? tnaRulesByPairId.get(group._id)
                          : undefined;
                        // For the dev-logs button, fall back to ANY rule
                        // (incl. sum_of_sides / paused / completed) so a
                        // pair with historical activity is still
                        // clickable on the TNA tab.
                        const debugRuleId =
                          rule?._id
                          ?? (group._id
                            ? anyRulesByPairId.get(group._id)?._id
                            : undefined);
                        // Always look up by pairId first (backend uses
                        // pairId for position-only states), then fall
                        // back to rule._id for rule-based states. The store
                        // dual-keys both, so resolve the id the row reads.
                        const liveStateId =
                          (group._id && store.has(group._id)
                            ? group._id
                            : rule?._id ?? group._id)
                          ?? undefined;
                        const pairKey = group._id ?? group.pairName;
                        return (
                          <PositionRow
                            key={pairKey}
                            dataIndex={vi.index}
                            measureRef={tnaRowVirtualizer.measureElement}
                            group={group}
                            polyPos={findPos(group, 'polymarket')}
                            kalshiPos={findPos(group, 'kalshi')}
                            rule={rule}
                            debugRuleId={debugRuleId}
                            store={store}
                            liveStateId={liveStateId}
                            pairKey={pairKey}
                            isExpanded={expandedPairId === pairKey}
                            onToggleExpand={handleToggleExpand}
                            onTogglePlatform={handleTogglePlatform}
                            onSaveParams={handleSaveParams}
                            onApplySim={handleApplySim}
                            onUpdateExpiry={handleUpdateExpiry}
                            onOpenEnginePosition={handleOpenEnginePosition}
                          />
                        );
                      })}
                      {paddingBottom > 0 && (
                        <TableRow style={{ height: paddingBottom }}>
                          <TableCell
                            colSpan={100}
                            sx={{ p: 0, border: 'none' }}
                            aria-hidden
                          />
                        </TableRow>
                      )}
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Orders panel intentionally hidden for now while redesign is in progress. */}

      <EnginePositionDrawer
        open={enginePos !== null}
        onClose={handleCloseEnginePosition}
        believedPosition={
          enginePos ? getState(enginePos.ruleId)?.believedPosition ?? null : null
        }
        pairName={enginePos?.pairName}
      />
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Simulation Breakdown Panel                                          */
/* ------------------------------------------------------------------ */

const stepLabel = { fontSize: '0.6rem', color: 'grey.500' } as const;
const stepValue = { fontSize: '0.62rem', fontFamily: 'monospace' } as const;

const SimulationBreakdownPanel = ({ polyAsMaker, kalshiAsMaker, expiryDate, daysToExpiry }: {
  polyAsMaker: SimulationResult | null;
  kalshiAsMaker: SimulationResult | null;
  expiryDate?: string | Date | null;
  daysToExpiry?: number | null;
}) => {
  const renderDirection = (label: string, result: SimulationResult | null) => {
    if (!result?.breakdown) return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>No data</Typography>
      </Box>
    );
    const b = result.breakdown;
    return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5, color: '#42a5f5' }}>{label}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px' }}>
          <Typography sx={stepLabel}>1. Raw taker bid</Typography>
          <Typography sx={stepValue}>{b.rawTakerBid.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>2. Taker fee ({b.takerPlatform}, {(b.takerFeeRate * 100).toFixed(2)}%)</Typography>
          <Typography sx={stepValue}>-{b.takerFeeAmount.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>3. Net taker bid</Typography>
          <Typography sx={stepValue}>{b.netTakerBid.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>4. Min sell price (TNA={b.targetTna}%, {b.effectiveDays.toFixed(1)}d)</Typography>
          <Typography sx={stepValue}>{b.minSellPrice.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>5. Best ask on maker book</Typography>
          <Typography sx={stepValue}>{b.makerAskTopPrice.toFixed(4)} ({Math.round(b.makerAskTopSize)} lots)</Typography>

          <Typography sx={stepLabel}>6. Maker price (at min sell price)</Typography>
          <Typography sx={{ ...stepValue, fontWeight: 600, color: '#42a5f5' }}>{b.makerPrice.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>7. Maker fee ({(b.makerFeeRate * 100).toFixed(2)}%)</Typography>
          <Typography sx={stepValue}>-{b.makerFeeAmount.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>8. Net maker proceeds</Typography>
          <Typography sx={stepValue}>{b.netMakerPrice.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>9. Size (req={b.requestedSize}, taker={Math.round(b.takerLiquidityCap)}, pos={b.positionCap || '\u221e'})</Typography>
          <Typography sx={stepValue}>{b.effectiveSize} contracts</Typography>

          <Typography sx={stepLabel}>10. Achieved TNA</Typography>
          <Typography sx={{ ...stepValue, fontWeight: 600, color: b.achievedTna >= 0 ? '#4caf50' : '#f44336' }}>
            {b.achievedTna.toFixed(2)}%
          </Typography>
        </Box>
        {b.unplaceableNoMillis && (
          <Typography sx={{ fontSize: '0.62rem', color: '#ff9800', mt: 0.5, fontWeight: 600 }}>
            Not placeable: minSellPrice &gt; 0.99 and market has no millis tick support
          </Typography>
        )}
      </Box>
    );
  };

  const expiryStr = expiryDate ? new Date(expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'unknown';
  const daysStr = daysToExpiry != null ? `${daysToExpiry.toFixed(1)} days` : 'unknown';

  return (
    <Box sx={{ py: 1, px: 2, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 1 }}>
      {/* Expiry header */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          <b>Expiry:</b> {expiryStr}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: daysToExpiry != null && daysToExpiry < 1 ? '#f44336' : 'grey.400' }}>
          <b>Days to expiry:</b> {daysStr}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          <b>Effective days:</b> {polyAsMaker?.breakdown?.effectiveDays?.toFixed(1) ?? kalshiAsMaker?.breakdown?.effectiveDays?.toFixed(1) ?? 'N/A'}
        </Typography>
      </Box>
      {/* Two-column breakdown */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {renderDirection('Polymarket as Maker', polyAsMaker)}
        <Box sx={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
        {renderDirection('Kalshi as Maker', kalshiAsMaker)}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Row                                                                 */
/* ------------------------------------------------------------------ */

interface PositionRowProps {
  /** Virtual-row index — drives `data-index` for react-virtual's measurer. */
  dataIndex: number;
  /** react-virtual's `measureElement` ref callback; lands on the main row. */
  measureRef: (el: HTMLTableRowElement | null) => void;
  group: PositionsGroupedByPair;
  polyPos?: PredictionPosition;
  kalshiPos?: PredictionPosition;
  rule?: UnwindingRule;
  /** Rule id to use when opening the dev-logs drawer. May resolve to a
   *  rule of any type/status (TNA, sum_of_sides, paused, completed) so
   *  logs stay reachable from the TNA tab even when the active tab's
   *  rule type doesn't match. Undefined when the pair has no rule at
   *  all (truly nothing to show). */
  debugRuleId?: string;
  /** The live store — the row subscribes to its own slice via `useLiveState`. */
  store: UnwindLiveStore;
  /** Resolved store key (pairId, falling back to ruleId) for this row's slice. */
  liveStateId?: string;
  pairKey: string;
  isExpanded: boolean;
  onToggleExpand: (pairKey: string) => void;
  onTogglePlatform: (rule: UnwindingRule | undefined, group: PositionsGroupedByPair, platform: 'kalshi' | 'polymarket', enabled: boolean) => Promise<void>;
  onSaveParams: (pairId: string | null | undefined, params: { tna: number; size: number; maxVol?: number }) => Promise<boolean>;
  onApplySim: (pairId: string | null | undefined) => Promise<boolean>;
  onUpdateExpiry: (pairId: string, expiryDate: string) => Promise<boolean>;
  /** Opens the "Engine position" drawer for this row's rule. */
  onOpenEnginePosition: (ruleId: string, pairName: string) => void;
}

const PositionRow = memo(({ dataIndex, measureRef, group, polyPos, kalshiPos, rule, debugRuleId, store, liveStateId, pairKey, isExpanded, onToggleExpand, onTogglePlatform, onSaveParams, onApplySim, onUpdateExpiry, onOpenEnginePosition }: PositionRowProps) => {
  // Per-row live slice — subscribes to just this pair's key so a WS frame
  // re-renders only the changed rows, not the whole table. Empty id (no
  // resolved key yet) yields `undefined`, same as a missing Map entry.
  const liveState = useLiveState(store, liveStateId ?? '');
  // Init from: liveState simParams (WS) > rule config (REST) > defaults
  const initTna = liveState?.simTna?.toString()
    ?? rule?.kalshiConfig?.targetTnaPercent?.toString()
    ?? rule?.polymarketConfig?.targetTnaPercent?.toString()
    ?? '';
  const initSize = liveState?.simSize?.toString()
    ?? rule?.kalshiConfig?.contractsToSell?.toString()
    ?? rule?.polymarketConfig?.contractsToSell?.toString()
    ?? '50';
  const initMaxVol = liveState?.simMaxVolume?.toString()
    ?? rule?.maxVolumeContracts?.toString()
    ?? '500';

  const [tna, setTna] = useState(initTna);
  const [size, setSize] = useState(initSize);
  const [maxVol, setMaxVol] = useState(initMaxVol);
  const [savedTna, setSavedTna] = useState(initTna);
  const [savedSize, setSavedSize] = useState(initSize);
  const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);

  // Boot inputs from WS exactly once, when the first enriched state arrives.
  // After that, the local savedX is authoritative: subsequent WS broadcasts
  // must NOT mutate the inputs.
  //
  // Why: the backend has two broadcast paths that can race — setSimParams
  // emits the fresh value immediately, while an orderbook-tick-driven
  // broadcastForPairId can have already computed (and is about to emit) a
  // state using the OLD simParams. The HTTP response reliably carries the
  // fresh value, so the client can receive [HTTP(new), WS(stale), WS(fresh)]
  // out of order. Any sync-on-every-tick effect will revert the user's just-
  // saved input to the stale value, which is the "it updates, then goes back"
  // bug. Locking to a one-shot sync eliminates the whole class of revert.
  const hasBootedFromWsRef = useRef(false);
  useEffect(() => {
    if (hasBootedFromWsRef.current) return;
    if (liveState?.simTna == null) return;
    hasBootedFromWsRef.current = true;
    const wsTna = liveState.simTna.toString();
    if (wsTna !== savedTna) {
      setTna(wsTna);
      setSavedTna(wsTna);
    }
    if (liveState.simSize != null) {
      const wsSize = liveState.simSize.toString();
      if (wsSize !== savedSize) {
        setSize(wsSize);
        setSavedSize(wsSize);
      }
    }
    if (liveState.simMaxVolume != null) {
      const wsMaxVol = liveState.simMaxVolume.toString();
      if (wsMaxVol !== savedMaxVol) {
        setMaxVol(wsMaxVol);
        setSavedMaxVol(wsMaxVol);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveState?.simTna]);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [togglingPoly, setTogglingPoly] = useState(false);
  const [togglingKalshi, setTogglingKalshi] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ platform: 'kalshi' | 'polymarket'; enabled: boolean } | null>(null);
  const currentExpirySource = liveState?.expiryDate ?? group.expiryDate ?? null;
  const [expiryInput, setExpiryInput] = useState(isoToLocalInput(currentExpirySource));
  const [expirySaving, setExpirySaving] = useState(false);
  const [expiryError, setExpiryError] = useState<string | null>(null);
  const [expiryDirty, setExpiryDirty] = useState(false);

  useEffect(() => {
    if (expiryDirty) return;
    setExpiryInput(isoToLocalInput(currentExpirySource));
  }, [currentExpirySource, expiryDirty]);

  const isDirty = tna !== savedTna || size !== savedSize || maxVol !== savedMaxVol;
  const hasPendingSim = !!liveState?.hasPendingSim;
  const hasPendingTna = hasPendingSim
    && Number.isFinite(liveState?.simTna)
    && Number.isFinite(liveState?.appliedSimTna)
    && Math.abs((liveState?.simTna ?? 0) - (liveState?.appliedSimTna ?? 0)) > 1e-9;

  const handleApplySim = useCallback(async () => {
    if (applying || isDirty) return;
    setApplying(true);
    try {
      await onApplySim(group._id);
    } finally {
      setApplying(false);
    }
  }, [applying, group._id, isDirty, onApplySim]);

  const pendingTooltip = hasPendingTna ? (
    <Box>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
        Simulation differs from live order
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
        Applied: tna {liveState?.appliedSimTna ?? '—'}%, size {liveState?.appliedSimSize ?? '—'}, maxVol {liveState?.appliedSimMaxVolume ?? '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'warning.main' }}>
        Pending: tna {liveState?.simTna ?? '—'}%, size {liveState?.simSize ?? '—'}, maxVol {liveState?.simMaxVolume ?? '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.6rem', color: 'grey.500', mt: 0.3 }}>
        Click the publish icon to apply.
      </Typography>
    </Box>
  ) : '';

  const handleConfirmParams = useCallback(async () => {
    if (!isDirty) return;
    const previousTna = parseFloat(savedTna);
    const parsedTna = parseFloat(tna);
    const parsedSize = parseInt(size);
    const parsedMaxVol = parseFloat(maxVol);
    const safeTna = Number.isFinite(parsedTna) ? parsedTna : 5;
    const safeSize = Number.isFinite(parsedSize) ? parsedSize : 50;
    const safeMaxVol = Number.isFinite(parsedMaxVol) ? parsedMaxVol : undefined;
    const safePreviousTna = Number.isFinite(previousTna) ? previousTna : 5;
    const tnaChanged = Math.abs(safeTna - safePreviousTna) > 1e-9;
    const isRuleActive = rule?.status === 'active';

    setSaving(true);
    const ok = await onSaveParams(group._id, {
      tna: safeTna,
      size: safeSize,
      maxVol: safeMaxVol
    });
    if (ok) {
      setSavedTna(tna);
      setSavedSize(size);
      setSavedMaxVol(maxVol);

      // Auto-apply rules:
      //   - No rule / paused rule → apply immediately (no live order to gate).
      //   - Active rule + size/maxVol only → apply immediately (safe amend).
      //   - Active rule + TNA change → wait for explicit publish click (risk knob).
      const shouldAutoApply = !isRuleActive || !tnaChanged;
      if (shouldAutoApply) {
        setApplying(true);
        try {
          await onApplySim(group._id);
        } finally {
          setApplying(false);
        }
      }
    }
    // Wait for injected state to propagate before removing spinner
    await new Promise((r) => setTimeout(r, 100));
    setSaving(false);
  }, [group._id, isDirty, maxVol, onApplySim, onSaveParams, rule?.status, savedTna, size, tna]);

  const doToggle = useCallback(
    async (platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      const setLoading =
        platform === 'kalshi' ? setTogglingKalshi : setTogglingPoly;
      setLoading(true);
      try {
        // Mutation already does an optimistic cache patch in its
        // `onMutate`, so the Switch's `checked` flips instantly even
        // before the server responds. The spinner sits on top until
        // the HTTP round-trip finishes — no fixed 2s timer.
        await onTogglePlatform(rule, group, platform, enabled);
      } catch {
        // Rollback handled by the mutation's onError; nothing to do here.
      } finally {
        setLoading(false);
      }
    },
    [group, onTogglePlatform, rule],
  );

  const handleToggle = useCallback((platform: 'kalshi' | 'polymarket', enabled: boolean) => {
    if (enabled) {
      setConfirmDialog({ platform, enabled });
    } else {
      doToggle(platform, false);
    }
  }, [doToggle]);

  const handleConfirmToggle = useCallback(() => {
    if (confirmDialog) {
      doToggle(confirmDialog.platform, confirmDialog.enabled);
      setConfirmDialog(null);
    }
  }, [confirmDialog, doToggle]);

  const handleSaveExpiry = useCallback(async () => {
    if (!group._id || expirySaving || !expiryDirty) return;
    const nextExpiryIso = localInputToUtcIso(expiryInput);
    if (!nextExpiryIso) {
      setExpiryError('Invalid date');
      return;
    }
    setExpirySaving(true);
    setExpiryError(null);
    try {
      const ok = await onUpdateExpiry(group._id, nextExpiryIso);
      if (!ok) {
        setExpiryError('Failed to save');
      } else {
        setExpiryDirty(false);
      }
    } finally {
      setExpirySaving(false);
    }
  }, [expiryDirty, expiryInput, expirySaving, group._id, onUpdateExpiry]);

  const pBid = liveState?.polyBid; const pAsk = liveState?.polyAsk;
  const kBid = liveState?.kalshiBid; const kAsk = liveState?.kalshiAsk;
  const sim = liveState?.simulation;

  const inSx = {
    '& .MuiInputBase-input': { fontSize: '0.62rem', py: 0.15, px: 0.2, textAlign: 'right', width: 28 },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasPendingTna ? 'warning.main' : 'rgba(255,255,255,0.08)',
      borderWidth: hasPendingTna ? 1.5 : 1,
    },
    backgroundColor: hasPendingTna ? 'rgba(255,167,38,0.08)' : undefined,
    minWidth: 0,
  };

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontSize: '0.7rem' }}>{group.polymarketTitle || group.pairName}</Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>{group.kalshiTitle || kalshiPos?.marketId || group.pairName}</Typography>
    </Box>
  );

  return (
    <>
    <TableRow
      ref={measureRef}
      data-index={dataIndex}
      sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}
    >
      {/* Market */}
      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
          <Box onClick={() => onToggleExpand(pairKey)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start' }}>
            {isExpanded ? <KeyboardArrowDownIcon sx={{ fontSize: 14, mt: 0.2, color: 'grey.500' }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 14, mt: 0.2, color: 'grey.500' }} />}
          </Box>
          {/* Engine believed-position drawer trigger — what the live engine
              thinks we hold right now, ahead of the slow position sync. */}
          <Tooltip title={debugRuleId ? 'Engine position' : 'No rule yet'} placement="top">
            <span>
              <IconButton
                size="small"
                disabled={!debugRuleId}
                onClick={(e) => {
                  e.stopPropagation();
                  if (debugRuleId) onOpenEnginePosition(debugRuleId, group.pairName ?? '');
                }}
                sx={{ p: 0, ml: 0.2, color: debugRuleId ? 'grey.500' : 'grey.700' }}
              >
                <Inventory2Icon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        <Tooltip title={tooltipTitle} placement="top-start">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <a href={polyPos?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
                <img src="/assets/exchanges/png/100x100/polymarket.png" alt="P" style={{ width: 13, height: 13, borderRadius: '50%' }} />
              </a>
              <a
                href={polyPos?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
                style={{ flex: 1, minWidth: 0, overflow: 'hidden', color: 'inherit', textDecoration: 'none' }}
              >
                <Typography sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                  {group.polymarketTitle || group.pairName}
                </Typography>
              </a>
              {polyPos && <Chip label={polyPos.side.toUpperCase()} size="small" color={polyPos.side === 'yes' ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.42rem', height: 12, '& .MuiChip-label': { px: 0.25 }, opacity: 0.7 }} />}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <a href={kalshiPos?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
                <img src="/assets/exchanges/png/100x100/kalshi.png" alt="K" style={{ width: 13, height: 13, borderRadius: '50%' }} />
              </a>
              <a
                href={kalshiPos?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
                style={{ flex: 1, minWidth: 0, overflow: 'hidden', color: 'inherit', textDecoration: 'none' }}
              >
                <Typography sx={{ fontSize: '0.6rem', color: 'grey.400', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                  {group.kalshiTitle || kalshiPos?.marketId || group.pairName}
                </Typography>
              </a>
              {kalshiPos && <Chip label={kalshiPos.side.toUpperCase()} size="small" color={kalshiPos.side === 'yes' ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.42rem', height: 12, '& .MuiChip-label': { px: 0.25 }, opacity: 0.7 }} />}
            </Box>
          </Box>
        </Tooltip>
        </Box>
      </TableCell>

      {/* Position size — both platforms stacked, right-aligned */}
      <TableCell sx={{ ...cSx, verticalAlign: 'top' }} align="right">
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>{fmtContracts(polyPos?.contracts)}</Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>{fmtContracts(kalshiPos?.contracts)}</Typography>
      </TableCell>

      {/* Live engine NET (signed YES − NO) — red when unhedged. */}
      <TableCell sx={{ ...cSx, verticalAlign: 'top', fontSize: '0.6rem', fontWeight: 700 }} align="right">
        <NetCell bp={liveState?.believedPosition ?? null} rest={group} />
      </TableCell>

      {/* Actual NET from held positions (REST) — independent of the live
          engine belief, which reads 0 for a pre-existing position. */}
      <TableCell sx={{ ...cSx, verticalAlign: 'top', fontSize: '0.6rem', fontWeight: 700 }} align="right">
        <RestNetCell
          rest={{ positions: group.positions.filter(p => p.status === 'open' && (p.contracts ?? 0) > 0) }}
        />
      </TableCell>

      {/* Market Data — read price AND size from the same TopOfBook so a row
          never renders "price without size". The position-unwinder owns the
          books (direct WS to Kalshi/Polymarket); the REST seed fills the
          state on mount. If the TopOfBook is null the cell is empty in BOTH
          columns — consistent and unambiguous. */}
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pBid?.size)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pBid?.price)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pAsk?.price)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pAsk?.size)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kBid?.size)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kBid?.price)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kAsk?.price)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kAsk?.size)}</Typography></TableCell>

      {/* Parameters (editable) + confirm button */}
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Tooltip title={pendingTooltip} placement="top" disableHoverListener={!hasPendingTna}>
          <TextField variant="outlined" size="small" value={tna} placeholder="5" onChange={e => setTna(e.target.value)} onBlur={handleConfirmParams} onKeyDown={e => { if (e.key === 'Enter') handleConfirmParams(); }} sx={inSx} />
        </Tooltip>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Tooltip title={pendingTooltip} placement="top" disableHoverListener={!hasPendingTna}>
          <TextField variant="outlined" size="small" value={size} placeholder="50" onChange={e => setSize(e.target.value)} onBlur={handleConfirmParams} onKeyDown={e => { if (e.key === 'Enter') handleConfirmParams(); }} sx={inSx} />
        </Tooltip>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <Tooltip title={pendingTooltip} placement="top" disableHoverListener={!hasPendingTna}>
            <TextField variant="outlined" size="small" value={maxVol} placeholder="500" onChange={e => setMaxVol(e.target.value)} onBlur={handleConfirmParams} onKeyDown={e => { if (e.key === 'Enter') handleConfirmParams(); }} sx={inSx} />
          </Tooltip>
          {saving ? (
            <CircularProgress size={12} sx={{ ml: 0.2 }} />
          ) : isDirty ? (
            <IconButton size="small" onClick={handleConfirmParams} sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}>
              <CheckIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          ) : null}
          {hasPendingTna && !isDirty && (
            applying ? (
              <CircularProgress size={12} sx={{ ml: 0.2, color: 'warning.main' }} />
            ) : (
              <Tooltip title="Apply sim to live order" placement="top">
                <IconButton size="small" onClick={handleApplySim} sx={{ p: 0.1, ml: 0.2, color: 'warning.main' }}>
                  <PublishIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
              </Tooltip>
            )
          )}
        </Stack>
      </TableCell>

      {/* Simulation — dim while recalculating, spinner in first cell */}
      <TableCell sx={cSx} align="right">
        {saving
          ? <CircularProgress size={10} sx={{ color: 'grey.500' }} />
          : <Typography sx={{ fontSize: '0.7rem', color: '#ff9800' }}>{fmtPrice(sim?.polyAsMaker?.takerPrice)}</Typography>}
      </TableCell>
      <TableCell sx={cSx} align="right" title={sim?.polyAsMaker?.breakdown?.unplaceableNoMillis ? 'Not placeable: minSellPrice > 0.99 and market has no millis tick support' : undefined}><Typography sx={{ fontSize: '0.7rem', color: sim?.polyAsMaker?.breakdown?.unplaceableNoMillis ? '#ff9800' : '#42a5f5', fontWeight: 600, opacity: saving ? 0.3 : 1, textDecoration: sim?.polyAsMaker?.breakdown?.unplaceableNoMillis ? 'line-through' : undefined }}>{fmtPrice(sim?.polyAsMaker?.makerPrice)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.7rem', color: '#ff9800', opacity: saving ? 0.3 : 1 }}>{fmtPrice(sim?.kalshiAsMaker?.takerPrice)}</Typography></TableCell>
      <TableCell sx={cSx} align="right" title={sim?.kalshiAsMaker?.breakdown?.unplaceableNoMillis ? 'Not placeable: minSellPrice > 0.99 and market has no millis tick support' : undefined}><Typography sx={{ fontSize: '0.7rem', color: sim?.kalshiAsMaker?.breakdown?.unplaceableNoMillis ? '#ff9800' : '#42a5f5', fontWeight: 600, opacity: saving ? 0.3 : 1, textDecoration: sim?.kalshiAsMaker?.breakdown?.unplaceableNoMillis ? 'line-through' : undefined }}>{fmtPrice(sim?.kalshiAsMaker?.makerPrice)}</Typography></TableCell>

      {/* Controls */}
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingPoly ? <CircularProgress size={14} /> : (
          <Switch size="small" checked={rule ? rule.polymarketEnabled !== false : false} onChange={(_, v) => handleToggle('polymarket', v)} />
        )}
      </TableCell>
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingKalshi ? <CircularProgress size={14} /> : (
          <Switch size="small" checked={rule ? rule.kalshiEnabled !== false : false} onChange={(_, v) => handleToggle('kalshi', v)} />
        )}
      </TableCell>

    </TableRow>

    {/* Expanded simulation breakdown */}
    {isExpanded && sim && (
      <TableRow data-expansion-index={dataIndex}>
        <TableCell colSpan={20} sx={{ py: 0, px: 1, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <SimulationBreakdownPanel
            polyAsMaker={sim.polyAsMaker}
            kalshiAsMaker={sim.kalshiAsMaker}
            expiryDate={liveState?.expiryDate ?? group.expiryDate}
            daysToExpiry={liveState?.daysToExpiry ?? group.daysToExpiry}
          />
          {group._id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 1.2, pt: 0.3 }}>
              <TextField
                size="small"
                type="datetime-local"
                label="Pair expiry (saved as UTC)"
                value={expiryInput}
                onChange={(e) => {
                  setExpiryInput(e.target.value);
                  setExpiryDirty(true);
                  setExpiryError(null);
                }}
                onBlur={handleSaveExpiry}
                sx={{
                  minWidth: 250,
                  '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.45 },
                  '& .MuiInputLabel-root': { fontSize: '0.72rem' },
                }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveExpiry}
                disabled={expirySaving || !expiryDirty}
                sx={{ fontSize: '0.7rem', minWidth: 72 }}
              >
                {expirySaving ? <CircularProgress size={12} /> : 'Save'}
              </Button>
              {expiryError && (
                <Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>
                  {expiryError}
                </Typography>
              )}
            </Box>
          )}
        </TableCell>
      </TableRow>
    )}

    {/* Confirmation dialog */}
    <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs">
      <DialogTitle sx={{ fontSize: '0.9rem', pb: 0.5 }}>
        Activate {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'} Maker
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Market:</b> {group.pairName}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Platform:</b> {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'} (as maker)</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Target TNA:</b> {tna || '5'}%</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Order size:</b> {size || '50'} contracts</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Max volume:</b> ${maxVol || '500'}/day</Typography>
          {confirmDialog?.platform === 'kalshi' && kalshiPos && (
            <Typography sx={{ fontSize: '0.8rem' }}><b>Position:</b> {kalshiPos.side.toUpperCase()} {kalshiPos.contracts} @ {kalshiPos.avgPrice.toFixed(2)}</Typography>
          )}
          {confirmDialog?.platform === 'polymarket' && polyPos && (
            <Typography sx={{ fontSize: '0.8rem' }}><b>Position:</b> {polyPos.side.toUpperCase()} {polyPos.contracts} @ {polyPos.avgPrice.toFixed(2)}</Typography>
          )}
          <Typography sx={{ fontSize: '0.75rem', color: 'warning.main', mt: 0.5 }}>
            This will start placing real orders on {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog(null)} size="small">Cancel</Button>
        <Button onClick={handleConfirmToggle} variant="contained" color="success" size="small">Activate</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}, (prev, next) => (
  // `dataIndex` must be compared so the row re-renders (and re-stamps its
  // `data-index`) when a sort reorders it — react-virtual keys measured
  // heights off that attribute. `measureRef` is a stable callback.
  prev.dataIndex === next.dataIndex
  && prev.group === next.group
  && prev.polyPos === next.polyPos
  && prev.kalshiPos === next.kalshiPos
  && prev.rule === next.rule
  && prev.debugRuleId === next.debugRuleId
  // Live state is no longer a prop — the row reads it via `useLiveState`
  // off the store. Compare the store handle + resolved key so a re-key
  // (rare: rule (un)wraps a pair) still re-subscribes the row.
  && prev.store === next.store
  && prev.liveStateId === next.liveStateId
  && prev.pairKey === next.pairKey
  && prev.isExpanded === next.isExpanded
  && prev.onToggleExpand === next.onToggleExpand
  && prev.onTogglePlatform === next.onTogglePlatform
  && prev.onSaveParams === next.onSaveParams
  && prev.onApplySim === next.onApplySim
  && prev.onUpdateExpiry === next.onUpdateExpiry
  && prev.onOpenEnginePosition === next.onOpenEnginePosition
));
